<script lang="ts">
    import { onMount } from "svelte";
    import Card, { getCards } from "./Card";
    import { getBlocks } from "./Parser";
    import IdManager from "../tools/IdManager";
    import ContentEditable from "../text/ContentEditable.svelte";
    import DropdownIcon from "../components/icons/DropdownIcon.svelte";
    import IconButton from "../components/buttons/IconButton.svelte";
    import AddIcon from "../components/icons/AddIcon.svelte";
    import DropdownMenu from "../components/menu/dropdown/DropdownMenu.svelte";
    import DropdownMenuItem from "../components/menu/dropdown/DropdownMenuItem.svelte";
    import DeleteIcon from "../components/icons/DeleteIcon.svelte";
    import EditIcon from "../components/icons/EditIcon.svelte";

    export let screenplay: string;

    const idManagerBlocks = new IdManager("tmpblock");
    const idManagerCards = new IdManager("Card");

    let cards: Card[] = getCards(
        getBlocks(screenplay, idManagerBlocks),
        idManagerCards
    );
    let controlKey: "control" | "shift" | null = null;
    let selectedIds: string[] = [];
    let lastTouchedCardForSelection: string | null = null;
    let hoveringDropable:
        | { cardId: string; type: "before" | "after" }
        | undefined;

    let dropDownOpenCardId: string | null = null;

    let cardSize: "s" | "m" | "l" | "xl" | "xxl" = "l";

    export let setScreenplay: (string: string) => void;
    export let editScreenplay: (index: number) => void;
    export let toggleUnsavedChanges: () => void;
    export const transferToParent = () => {
        setScreenplay(
            cards
                .map((card) => {
                    return card
                        .getAsBlocks(idManagerBlocks)
                        .map((block) => {
                            return block.getText();
                        })
                        .join("\n");
                })
                .join("\n")
        );
    };

    const clearSelection = () => {
        selectedIds = [];
    };

    function getSortedIndicesForSelectedCards(): number[] {
        const indicesToMove: number[] = [];
        selectedIds.forEach((cardId) => {
            indicesToMove.push(
                cards.findIndex((candidate) => {
                    return candidate.getId() === cardId;
                })
            );
        });
        indicesToMove.sort(function (a, b) {
            return a - b;
        });

        return indicesToMove;
    }

    onMount(() => {
        const keyDown = (e: KeyboardEvent) => {
            if (e.key === "Shift") {
                controlKey = "shift";
            } else if (e.key === "Control") {
                controlKey = "control";
            }
        };
        const keyUp = (e: KeyboardEvent) => {
            if (e.key === "Shift" || e.key === "Control") {
                controlKey = null;
            }
        };
        window.addEventListener("keydown", keyDown);
        window.addEventListener("keyup", keyUp);
        window.addEventListener("click", clearSelection);

        return () => {
            window.removeEventListener("keydown", keyDown);
            window.removeEventListener("keyup", keyUp);
            window.removeEventListener("click", clearSelection);
        };
    });

    let selectionDiv: HTMLElement;

    function isIntersecting(
        elementA: HTMLElement,
        elementB: HTMLElement
    ): boolean {
        const rectA = elementA.getBoundingClientRect();
        const rectB = elementB.getBoundingClientRect();
        return (
            rectA.top + rectA.height > rectB.top &&
            rectA.left + rectA.width > rectB.left &&
            rectA.bottom - rectA.height < rectB.bottom &&
            rectA.right - rectA.width < rectB.right
        );
    }

    function thresholdReached(dx: number, dy: number): boolean {
        const thresholdPixels = 10;
        const thresholdXReached =
            (dx > 0 && dx >= thresholdPixels) ||
            (dx < 0 && dx <= thresholdPixels * -1);
        const thresholdYReached =
            (dy > 0 && dy >= thresholdPixels) ||
            (dy < 0 && dy <= thresholdPixels * -1);

        return thresholdXReached || thresholdYReached;
    }

    function selectionContainer(node: HTMLElement) {
        let selectionActive = false,
            initialPosX = 0,
            initialPosY = 0,
            x = 0,
            y = 0;

        node.addEventListener("click", (e) => {
            if (selectedIds.length > 0) {
                e.stopPropagation();
            }
        });

        function mousemove(event: MouseEvent) {
            const rect = node.getBoundingClientRect();

            let dx = event.clientX - initialPosX;
            let dy = event.clientY - initialPosY;

            if (!selectionActive && thresholdReached(dx, dy)) {
                selectionActive = true;
                selectionDiv.style.display = "block";
                selectionDiv.style.position = "absolute";
            }

            if (selectionActive) {
                selectionDiv.style.left = `${
                    (dx >= 0 ? initialPosX : initialPosX + dx) - rect.left
                }px`;
                selectionDiv.style.top = `${
                    (dy >= 0 ? initialPosY : initialPosY + dy) - 40 // 40 = top
                }px`;
                selectionDiv.style.width = `${dx >= 0 ? dx : dx * -1}px`;
                selectionDiv.style.height = `${dy >= 0 ? dy : dy * -1}px`;

                const event = new CustomEvent("moveSelection");
                window.dispatchEvent(event);
            }

            x = event.clientX;
            y = event.clientY;
            event.preventDefault();
        }

        function onMousedown(event: MouseEvent) {
            initialPosX = event.clientX;
            initialPosY = event.clientY;
            x = event.clientX;
            y = event.clientY;
            window.addEventListener("mousemove", mousemove);
            window.addEventListener("mouseup", onMouseUp);
            selectionDiv.style.zIndex = "1";
        }

        function onMouseUp(event: MouseEvent) {
            if (!selectionActive) {
                clearSelection();
            }
            selectionActive = false;
            window.removeEventListener("mousemove", mousemove);
            window.removeEventListener("mouseup", onMouseUp);
            x = 0;
            y = 0;
            selectionDiv.style.zIndex = "0";
            selectionDiv.style.display = "none";
            event.preventDefault();
            event.stopPropagation();
        }

        node.addEventListener("mousedown", onMousedown);
    }

    const selectablesById: { [key: string]: HTMLElement } = {};
    let moveIndicator: HTMLElement;
    function selectable(node: HTMLElement, card: Card) {
        selectablesById[card.getId()] = node;

        let x: number,
            y: number,
            moveActive = false;

        const mouseMove = (e: MouseEvent) => {
            let dx = e.clientX - x;
            let dy = e.pageY - y;
            if (!moveActive && thresholdReached(dx, dy)) {
                moveActive = true;
                if (selectedIds.indexOf(card.getId()) === -1) {
                    selectedIds = [card.getId()];
                }
                moveIndicator.innerHTML = `<span>${selectedIds.length}</span>`;
                moveIndicator.style.display = "block";
                moveIndicator.style.position = "fixed";
            }

            moveIndicator.style.left = `${e.clientX}px`;
            moveIndicator.style.top = `${e.clientY}px`;

            const event = new CustomEvent("moveCards");
            window.dispatchEvent(event);
        };

        const mouseUp = (e: MouseEvent) => {
            window.removeEventListener("mousemove", mouseMove);
            window.removeEventListener("mouseup", mouseUp);
            moveIndicator.style.display = "none";
            if (moveActive) {
                if (hoveringDropable) {
                    const cardsToMove: Card[] = [];
                    const indicesToMove = getSortedIndicesForSelectedCards();
                    indicesToMove.forEach((index) => {
                        cardsToMove.push(cards[index]);
                    });

                    const newCards: Card[] = [];
                    cards.forEach((card) => {
                        if (
                            card.getId() === hoveringDropable.cardId &&
                            "before" === hoveringDropable.type
                        ) {
                            newCards.push(...cardsToMove);
                        }
                        if (cardsToMove.indexOf(card) === -1) {
                            newCards.push(card);
                        }
                        if (
                            card.getId() === hoveringDropable.cardId &&
                            "after" === hoveringDropable.type
                        ) {
                            newCards.push(...cardsToMove);
                        }
                    });

                    cards = newCards;
                    toggleUnsavedChanges();
                }
                selectedIds = [];
            }

            hoveringDropable = undefined;
            moveActive = false;
        };

        const mouseDown = (e: MouseEvent) => {
            x = e.clientX;
            y = e.clientY;
            window.addEventListener("mousemove", mouseMove);
            window.addEventListener("mouseup", mouseUp);
        };

        node.addEventListener("mousedown", mouseDown);
    }
    onMount(() => {
        const moveSelectionListener = () => {
            const newSelectedIds = [];
            Object.entries(selectablesById).forEach((entry) => {
                if (isIntersecting(entry[1], selectionDiv)) {
                    newSelectedIds.push(entry[0]);
                }
            });
            selectedIds = newSelectedIds;
        };
        window.addEventListener("moveSelection", moveSelectionListener);

        return () => {
            window.removeEventListener("moveSelection", moveSelectionListener);
        };
    });

    let dropables: {
        cardId: string;
        type: "before" | "after";
        element: HTMLElement;
    }[] = [];
    function droppable(
        node: HTMLElement,
        options: { type: "before" | "after"; card: Card }
    ) {
        dropables = dropables.filter((dropable) => {
            return document.body.contains(dropable.element);
        });
        dropables.push({
            cardId: options.card.getId(),
            type: options.type,
            element: node,
        });
    }

    function isOver(dragged: HTMLElement, dropZone: HTMLElement) {
        const rectDragged = dragged.getBoundingClientRect();
        const rectDropZone = dropZone.getBoundingClientRect();

        // -20 = margin
        if (
            rectDragged.top > rectDropZone.top &&
            rectDragged.top < rectDropZone.bottom &&
            rectDragged.left - 20 > rectDropZone.left &&
            rectDragged.left - 20 < rectDropZone.right
        ) {
            return true;
        }

        return false;
    }

    onMount(() => {
        const moveCardsListener = () => {
            let hadHover = false;
            for (const droppable of dropables) {
                if (isOver(moveIndicator, droppable.element)) {
                    hoveringDropable = {
                        type: droppable.type,
                        cardId: droppable.cardId,
                    };
                    hadHover = true;
                    break;
                }
            }
            if (!hadHover) {
                hoveringDropable = undefined;
            }
        };
        window.addEventListener("moveCards", moveCardsListener);

        return () => {
            window.removeEventListener("moveCards", moveCardsListener);
        };
    });

    const onCardClick = (e: MouseEvent, card: Card) => {
        if ("shift" !== controlKey || selectedIds.length === 0) {
            lastTouchedCardForSelection = card.getId();
        }
        if (null === controlKey) {
            if (selectedIds.length === 1 && selectedIds[0] === card.getId()) {
                selectedIds = [];
            } else {
                selectedIds = [card.getId()];
            }
        } else if ("control" === controlKey) {
            const indexOfSelectedCard = selectedIds.indexOf(card.getId());
            if (indexOfSelectedCard > -1) {
                selectedIds.splice(indexOfSelectedCard, 1);
                selectedIds = selectedIds;
            } else {
                selectedIds.push(card.getId());
                selectedIds = selectedIds;
            }
        } else if ("shift" === controlKey) {
            if (
                selectedIds.length === 0 ||
                lastTouchedCardForSelection === null
            ) {
                selectedIds = [card.getId()];
                return;
            }
            const indexLastTouched = cards.findIndex((candidate) => {
                return lastTouchedCardForSelection === candidate.getId();
            });
            const indexShift = cards.findIndex((candidate) => {
                return card.getId() === candidate.getId();
            });
            let [start, end] = [indexLastTouched, indexShift];
            if (indexShift < indexLastTouched) {
                [start, end] = [indexShift, indexLastTouched];
            }
            const newSelectedIds = [];
            for (let i = start; i <= end; i++) {
                newSelectedIds.push(cards[i].getId());
            }
            selectedIds = newSelectedIds;
        }
    };

    function addCard(index: number) {
        const newCard = new Card(idManagerCards.getNextId(), []);
        newCard.setName("New");
        if (index === -1) {
            cards.unshift(newCard);
        } else {
            cards.splice(index, 0, newCard);
        }
        cards = cards;
        toggleUnsavedChanges();
    }

    function removeCard(index: number) {
        cards.splice(index, 1);
        cards = cards;
        toggleUnsavedChanges();
    }

    onMount(() => {
        const deleteCardsListener = (e: KeyboardEvent) => {
            if (e.key === "Delete" && selectedIds.length > 0) {
                e.preventDefault();
                e.stopPropagation();
                if (confirm(`Delete ${selectedIds.length} cards?`)) {
                    selectedIds.forEach((cardId) => {
                        const index = cards.findIndex((candidate) => {
                            return candidate.getId() === cardId;
                        });
                        cards.splice(index, 1);
                    });
                    cards = cards;
                    selectedIds = [];
                    toggleUnsavedChanges();
                }
            }
        };
        window.addEventListener("keydown", deleteCardsListener);

        return () => {
            window.removeEventListener("keydown", deleteCardsListener);
        };
    });
</script>

<div class="Board {cardSize}" use:selectionContainer>
    {#each cards as card, index (card.getId())}
        <div class="ItemContainer">
            <div
                class="DropZone DropZone--before {hoveringDropable &&
                hoveringDropable.cardId === card.getId() &&
                hoveringDropable.type === 'before'
                    ? 'over'
                    : ''}"
                use:droppable={{ card: card, type: "before" }}
            >
                <IconButton
                    icon={AddIcon}
                    title="Add index card"
                    onClick={() => {
                        addCard(index);
                    }}
                />
            </div>
            <div
                class="Item {selectedIds.indexOf(card.getId()) > -1
                    ? 'is-selected'
                    : ''}"
                on:mousedown|stopPropagation={(e) => {}}
                on:click|stopPropagation={(e) => {
                    onCardClick(e, card);
                }}
                use:selectable={card}
            >
                <div
                    on:click|stopPropagation={() => {}}
                    on:keydown={(e) => {
                        if (e.key === "Delete") {
                            e.stopPropagation();
                        }
                    }}
                >
                    <div class="CardHeader">
                        <input
                            type="text"
                            class="CardTitle"
                            spellcheck={false}
                            on:blur={(e) => {
                                card.setName(e.currentTarget.value);
                            }}
                            on:input={() => {
                                toggleUnsavedChanges();
                            }}
                            on:mousedown={(e) => {
                                e.stopPropagation();
                            }}
                            value={card.getName()}
                        />
                        <div
                            class="CardNav"
                            on:click|preventDefault|stopPropagation={() => {
                                if (dropDownOpenCardId === card.getId()) {
                                    dropDownOpenCardId = null;
                                } else {
                                    dropDownOpenCardId = card.getId();
                                }
                            }}
                        >
                            <DropdownIcon />
                            {#if dropDownOpenCardId === card.getId()}
                                <div
                                    style="position: absolute; top: 20px; right: 0;"
                                >
                                    <DropdownMenu
                                        closeCallback={() => {
                                            dropDownOpenCardId = null;
                                        }}
                                    >
                                        <DropdownMenuItem
                                            title="Delete"
                                            onClick={() => {
                                                if (
                                                    confirm(
                                                        `Delete ${card.getName()}?`
                                                    )
                                                ) {
                                                    removeCard(index);
                                                }
                                            }}
                                            icon={DeleteIcon}
                                        />
                                        <DropdownMenuItem
                                            title="Open in editor"
                                            onClick={() => {
                                                let indexToEdit = 0;
                                                for (const currentCard of cards) {
                                                    if (
                                                        currentCard.getId() ===
                                                        card.getId()
                                                    ) {
                                                        editScreenplay(
                                                            indexToEdit
                                                        );
                                                        break;
                                                    } else {
                                                        indexToEdit += currentCard.getBlockCount();
                                                    }
                                                }
                                            }}
                                            icon={EditIcon}
                                        />
                                    </DropdownMenu>
                                </div>
                            {/if}
                        </div>
                    </div>

                    <div
                        on:mousedown={(e) => {
                            e.stopPropagation();
                        }}
                    >
                        <ContentEditable
                            text={card.getDescription()}
                            onBlur={(text) => {
                                card.setDescription(text);
                                cards = cards;
                            }}
                            {toggleUnsavedChanges}
                        />
                    </div>
                </div>
            </div>
            <div
                class="DropZone DropZone--after {hoveringDropable &&
                hoveringDropable.cardId === card.getId() &&
                hoveringDropable.type === 'after'
                    ? 'over'
                    : ''}"
                use:droppable={{ card: card, type: "after" }}
            >
                {#if index === cards.length - 1}
                    <IconButton
                        icon={AddIcon}
                        title="Add index card"
                        onClick={() => {
                            addCard(index + 1);
                        }}
                    />
                {/if}
            </div>
        </div>
    {:else}
        No cards.
        <div
            class="AddButton"
            on:click={() => {
                addCard(0);
            }}
        >
            +
        </div>
    {/each}
    <div class="Selection" bind:this={selectionDiv} />
    <div class="MoveIndicator" bind:this={moveIndicator} />
</div>
<div class="SizeSelector">
    <button
        class="SelectSize"
        class:active={cardSize === "s"}
        on:click={() => {
            cardSize = "s";
        }}>S</button
    >
    <button
        class="SelectSize"
        class:active={cardSize === "m"}
        on:click={() => {
            cardSize = "m";
        }}>M</button
    >
    <button
        class="SelectSize"
        class:active={cardSize === "l"}
        on:click={() => {
            cardSize = "l";
        }}>L</button
    >
    <button
        class="SelectSize"
        class:active={cardSize === "xl"}
        on:click={() => {
            cardSize = "xl";
        }}>XL</button
    >
    <button
        class="SelectSize"
        class:active={cardSize === "xxl"}
        on:click={() => {
            cardSize = "xxl";
        }}>XXL</button
    >
</div>

<style type="text/scss">
    @import "../scss/config.scss";
    .SizeSelector {
        position: fixed;
        right: 10px;
        bottom: 5px;
        z-index: 1;
    }
    .SelectSize {
        background: $color-very-light-grey;
        margin-left: 5px;
        font-size: 0.75rem;
        cursor: pointer;
        &.active {
            background: $color-light-grey;
            border: 1px solid $color-medium-dark-grey;
        }
    }
    .Selection {
        border: 1px solid rgb(206, 28, 28);
        background: rgba(170, 170, 170, 0.5);
        position: absolute;
        display: none;
    }
    .Board {
        display: flex;
        flex-wrap: wrap;
        width: 100%;
        padding: 0 20px 0 40px;
        box-sizing: border-box;
        min-height: 90vh;
        align-items: stretch;
        justify-content: flex-start;
        align-content: flex-start;
    }
    .DropZone {
        width: 20px;
        height: 100%;
        position: relative;
    }
    :global(.DropZone.over) {
        &:before {
            content: "";
            margin-left: -1px;
            width: 2px;
            height: 100%;
            background: rgb(85, 85, 182);
            position: absolute;
            top: 0;
            left: 50%;
        }
    }
    .Item {
        box-sizing: border-box;
        padding: 10px;
        border: 2px solid #d0d0d0;
        box-shadow: 0px 6px 10px -6px rgb(0 0 0 / 20%);
        flex-grow: 1;
        user-select: none;
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        max-width: calc(100% - 40px);
    }
    .Item.is-selected {
        border: 2px solid rgb(85, 85, 182);
    }
    .ItemContainer {
        display: flex;
        min-width: 400px;
        max-width: 400px;
        margin-bottom: 40px;
    }
    .Board.s {
        .ItemContainer {
            min-width: 200px;
            max-width: 200px;
        }
        .Item {
            zoom: 0.6;
        }
    }
    .Board.m {
        .ItemContainer {
            min-width: 300px;
            max-width: 300px;
        }
        .Item {
            zoom: 0.7;
        }
    }
    .Board.xl {
        .ItemContainer {
            min-width: 500px;
            max-width: 500px;
        }
    }
    .Board.xxl {
        .ItemContainer {
            min-width: 600px;
            max-width: 600px;
        }
    }
    .MoveIndicator {
        margin-left: 20px;
        width: 1.5rem;
        height: 1.5rem;
        background: #fff;
        box-shadow: 0 0 8px #ccc;
        position: fixed;
        display: none;
        text-align: center;
        font-size: 1rem;
        line-height: 1.5rem;
        white-space: nowrap;
        color: rgb(85, 85, 182);
        border: 1px solid rgb(85, 85, 182);
        font-weight: bold;
    }
    .CardNav {
        position: relative;
        width: 24px;
        height: 24px;
        text-align: center;
        cursor: pointer;
        &:hover {
            background-color: rgba(0, 0, 0, 0.04);
            box-shadow: 0 0 0 5px rgb(0 0 0 / 4%);
            border-radius: 50%;
        }
    }
    .CardHeader {
        display: flex;
    }
    .CardTitle {
        width: 100%;
        flex-grow: 1;
        border: none;
        border-bottom: 1px solid rgb(177, 177, 177);
        padding: 5px;
        border-radius: 0;
        font-family: "sans_regular", sans-serif;
        font-size: 16px;
        &:focus {
            outline: 1px solid #d0d0d0;
            border-bottom: 1px solid #fff;
        }
    }
    :global(.Item .ContentEditable) {
        line-height: 22px;
        min-height: 22px * 4 + 5 + 5;
        max-height: 22px * 4 + 5 + 5;
        overflow: auto;
        -webkit-overflow-scrolling: touch;
        -ms-overflow-style: -ms-autohiding-scrollbar;
        &::-webkit-scrollbar {
            width: 4px;
        }

        &::-webkit-scrollbar-track {
            background: #ffffff25;
        }

        &::-webkit-scrollbar-thumb {
            width: 4px;
            background: rgba(0, 0, 0, 0.2);
            border-radius: 4px;
        }

        &::-webkit-scrollbar-thumb:hover {
            background: rgba(0, 0, 0, 0.5);
        }
    }
    :global(.xl .Item .ContentEditable) {
        min-height: 22px * 6 + 5 + 5;
        max-height: 22px * 6 + 5 + 5;
    }
    :global(.xxl .Item .ContentEditable) {
        min-height: 22px * 8 + 5 + 5;
        max-height: 22px * 8 + 5 + 5;
    }
    :global(.DropZone .IconButton) {
        position: absolute;
        right: 100%;
        top: 50%;
        margin-top: -12px !important;
        margin-right: -12px !important;
    }
    :global(.DropZone--after .IconButton) {
        margin-right: -30px !important;
    }
</style>
